import React, { useState, useEffect } from 'react';
import { Button } from '@/components/common/button';
import { Input } from '@/components/common/input';
import { Modal } from '@/components/common/modal';
import {
  Gauge,
  MapPin,
  User,
  Building,
  Hash,
  Calendar,
  AlertCircle
} from 'lucide-react';
import { Meter, MeterStatus } from '@/types/meter_types';

interface MeterFormProps {
  is_open: boolean;
  on_close: () => void;
  on_submit: (data: MeterFormData) => void;
  meter?: Meter | null;
  is_loading?: boolean;
  clients: Array<{ id: string; name: string }>;
}

export interface MeterFormData {
  meter_number: string;
  client_id: string;
  location: string;
  location_description?: string;
  property_name?: string;
  unit_number?: string;
  status: MeterStatus;
  installation_date?: string;
  initial_reading?: number;
}

export const MeterForm: React.FC<MeterFormProps> = ({
  is_open,
  on_close,
  on_submit,
  meter,
  is_loading = false,
  clients
}) => {
  const [form_data, set_form_data] = useState<MeterFormData>({
    meter_number: '',
    client_id: '',
    location: '',
    location_description: '',
    property_name: '',
    unit_number: '',
    status: 'active',
    installation_date: new Date().toISOString().split('T')[0],
    initial_reading: 0
  });

  const [errors, set_errors] = useState<Record<string, string>>({});

  // Reset form when modal opens/closes or meter changes
  useEffect(() => {
    if (is_open) {
      if (meter) {
        // Edit mode - populate with meter data
        // Normalize status to uppercase
        let normalized_status: MeterStatus = 'active';
        if (meter.status) {
          const status_upper = meter.status.toUpperCase();
          if (['ACTIVE', 'INACTIVE', 'FAULTY'].includes(status_upper)) {
            normalized_status = status_upper as MeterStatus;
          }
        }

        set_form_data({
          meter_number: meter.meter_number,
          client_id: meter.client_id,
          location: meter.location || '',
          location_description: meter.location_description || '',
          property_name: meter.property_name || '',
          unit_number: meter.unit_number || '',
          status: normalized_status,
          installation_date: meter.installation_date 
            ? new Date(meter.installation_date).toISOString().split('T')[0]
            : new Date().toISOString().split('T')[0],
          initial_reading: meter.initial_reading || 0
        });
      } else {
        // Create mode - reset to defaults
        set_form_data({
          meter_number: '',
          client_id: '',
          location: '',
          location_description: '',
          property_name: '',
          unit_number: '',
          status: 'active',
          installation_date: new Date().toISOString().split('T')[0],
          initial_reading: 0
        });
      }
      set_errors({});
    }
  }, [is_open, meter]);

  const validate_form = (): boolean => {
    const new_errors: Record<string, string> = {};

    if (!form_data.meter_number.trim()) {
      new_errors.meter_number = 'Meter number is required';
    }

    if (!form_data.client_id) {
      new_errors.client_id = 'Client selection is required';
    }

    if (!form_data.location.trim()) {
      new_errors.location = 'Location is required';
    }

    if (!form_data.installation_date) {
      new_errors.installation_date = 'Installation date is required';
    } else {
      const selected_date = new Date(form_data.installation_date);
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      
      if (selected_date > today) {
        new_errors.installation_date = 'Installation date cannot be in the future';
      }
    }

    if (form_data.initial_reading !== undefined && form_data.initial_reading < 0) {
      new_errors.initial_reading = 'Initial reading cannot be negative';
    }

    set_errors(new_errors);
    return Object.keys(new_errors).length === 0;
  };

  const handle_submit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validate_form()) {
      on_submit(form_data);
    }
  };

  const handle_change = (field: keyof MeterFormData, value: any) => {
    set_form_data(prev => ({ ...prev, [field]: value }));

    // Clear error for this field
    if (errors[field]) {
      set_errors(prev => {
        const new_errors = { ...prev };
        delete new_errors[field];
        return new_errors;
      });
    }
  };

  const handle_close = () => {
    if (!is_loading) {
      on_close();
    }
  };

  const is_edit_mode = !!meter;

  return (
    <Modal
      is_open={is_open}
      on_close={handle_close}
      title={is_edit_mode ? 'Edit Meter' : 'Add New Meter'}
      size="lg"
      footer={
        <div className="flex gap-3 justify-end">
          <Button
            variant="secondary"
            onClick={handle_close}
            disabled={is_loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handle_submit}
            is_loading={is_loading}
          >
            {is_edit_mode ? 'Update Meter' : 'Add Meter'}
          </Button>
        </div>
      }
    >
      <form onSubmit={handle_submit} className="space-y-6">
        {/* Meter Information Section */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-charcoal uppercase tracking-wide">
            Meter Information
          </h3>

          <Input
            label="Meter Number"
            type="text"
            placeholder="e.g., MTR-001234"
            value={form_data.meter_number}
            onChange={(e) => handle_change('meter_number', e.target.value)}
            icon_left={<Hash size={18} />}
            error={errors.meter_number}
            is_required
            helper_text="Unique identifier for the meter"
          />

          <div>
            <label className="block text-sm font-medium text-charcoal mb-1.5">
              Status <span className="text-error">*</span>
            </label>
            <select
              value={form_data.status}
              onChange={(e) => handle_change('status', e.target.value as MeterStatus)}
              className="w-full px-4 py-2.5 border border-light-gray rounded-md focus:outline-none focus:ring-2 focus:ring-forest-green focus:border-forest-green transition-all bg-white text-charcoal"
            >
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="FAULTY">Faulty</option>
            </select>
            <p className="mt-1.5 text-xs text-medium-gray">
              Current operational status of the meter
            </p>
          </div>

          <Input
            label="Installation Date"
            type="date"
            value={form_data.installation_date}
            onChange={(e) => handle_change('installation_date', e.target.value)}
            icon_left={<Calendar size={18} />}
            error={errors.installation_date}
            is_required
            helper_text="Date when the meter was installed"
          />

          <Input
            label="Initial Reading"
            type="number"
            step="0.01"
            placeholder="0"
            value={form_data.initial_reading?.toString() || '0'}
            onChange={(e) => handle_change('initial_reading', parseFloat(e.target.value) || 0)}
            icon_left={<Gauge size={18} />}
            error={errors.initial_reading}
            helper_text="Starting meter reading value (usually 0 for new meters)"
          />
        </div>

        {/* Client & Location Section */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-charcoal uppercase tracking-wide">
            Client & Location
          </h3>

          <div>
            <label className="block text-sm font-medium text-charcoal mb-1.5">
              Client <span className="text-error">*</span>
            </label>
            <div className="relative">
              <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-medium-gray" />
              <select
                value={form_data.client_id}
                onChange={(e) => handle_change('client_id', e.target.value)}
                className={`w-full pl-10 pr-4 py-2.5 border ${
                  errors.client_id ? 'border-error' : 'border-light-gray'
                } rounded-md focus:outline-none focus:ring-2 focus:ring-forest-green focus:border-forest-green transition-all bg-white text-charcoal`}
              >
                <option value="">Select a client...</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>
            {errors.client_id && (
              <p className="mt-1.5 text-sm text-error flex items-center gap-1">
                <AlertCircle size={14} />
                {errors.client_id}
              </p>
            )}
            <p className="mt-1.5 text-xs text-medium-gray">
              Select the client this meter belongs to
            </p>
          </div>

          <Input
            label="Location"
            type="text"
            placeholder="e.g., Main Building, Unit 5"
            value={form_data.location}
            onChange={(e) => handle_change('location', e.target.value)}
            icon_left={<MapPin size={18} />}
            error={errors.location}
            is_required
            helper_text="Primary location identifier"
          />

          <Input
            label="Location Description"
            type="text"
            placeholder="e.g., Ground floor utility room"
            value={form_data.location_description}
            onChange={(e) => handle_change('location_description', e.target.value)}
            icon_left={<MapPin size={18} />}
            helper_text="Additional location details (optional)"
          />
        </div>

        {/* Property Details Section (Optional) */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-charcoal uppercase tracking-wide">
            Property Details (Optional)
          </h3>

          <Input
            label="Property Name"
            type="text"
            placeholder="e.g., Riverside Apartments"
            value={form_data.property_name}
            onChange={(e) => handle_change('property_name', e.target.value)}
            icon_left={<Building size={18} />}
            helper_text="Name of the property/building"
          />

          <Input
            label="Unit Number"
            type="text"
            placeholder="e.g., A-101"
            value={form_data.unit_number}
            onChange={(e) => handle_change('unit_number', e.target.value)}
            icon_left={<Hash size={18} />}
            helper_text="Specific unit or apartment number"
          />
        </div>
      </form>
    </Modal>
  );
};